/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasSeenTooltip, markTooltipSeen } from 'utils/tooltipCookies';
import {
  markAllEditorTooltipsSeen,
  resetAllEditorTooltips,
  notifyEditorTooltipsChanged,
} from 'utils/tooltips';

export default function useTooltipSequence(
  tooltips,
  {
    enabled = true,
    initialDelay = 400,
    stepDelay = 1500,
    visibilityThreshold = 0,
    visibilityDelay = 0,
    onTooltipStateChange,
  } = {},
) {
  const registryRef = useRef({});
  const timeoutRef = useRef(null);
  const visibilityDelayTimeoutRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [registryVersion, setRegistryVersion] = useState(0);
  const [viewportVersion, setViewportVersion] = useState(0);

  const clearVisibilityDelayTimeout = useCallback(() => {
    if (visibilityDelayTimeoutRef.current) {
      window.clearTimeout(visibilityDelayTimeoutRef.current);
      visibilityDelayTimeoutRef.current = null;
    }
  }, []);

  function getScrollParent(el) {
    let parent = el?.parentElement;

    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowX = style.overflowX;
      const overflowY = style.overflowY;
      const canScrollX = /(auto|scroll|overlay)/.test(overflowX);
      const canScrollY = /(auto|scroll|overlay)/.test(overflowY);

      if (canScrollX || canScrollY) return parent;
      parent = parent.parentElement;
    }

    return null;
  }

  function isElementVisibleInContainer(el, visibilityThreshold = 0.75) {
    if (!el) return false;

    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    const scrollParent = getScrollParent(el);

    let visibleWidth = 0;
    let visibleHeight = 0;

    if (!scrollParent) {
      const viewportRect = {
        left: 0,
        top: 0,
        right: window.innerWidth,
        bottom: window.innerHeight,
      };

      visibleWidth =
        Math.min(rect.right, viewportRect.right) -
        Math.max(rect.left, viewportRect.left);

      visibleHeight =
        Math.min(rect.bottom, viewportRect.bottom) -
        Math.max(rect.top, viewportRect.top);
    } else {
      const parentRect = scrollParent.getBoundingClientRect();

      visibleWidth =
        Math.min(rect.right, parentRect.right) -
        Math.max(rect.left, parentRect.left);

      visibleHeight =
        Math.min(rect.bottom, parentRect.bottom) -
        Math.max(rect.top, parentRect.top);
    }

    const clampedVisibleWidth = Math.max(0, visibleWidth);
    const clampedVisibleHeight = Math.max(0, visibleHeight);

    const visibleArea = clampedVisibleWidth * clampedVisibleHeight;
    const totalArea = rect.width * rect.height;

    if (totalArea <= 0) return false;

    const visibleRatio = visibleArea / totalArea;

    return visibleRatio >= visibilityThreshold;
  }

  const clearScheduledTimeout = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const registerTooltipTarget = useCallback((id, ref, overrides = {}) => {
    const prev = registryRef.current[id];

    const nextEntry = {
      ref,
      ...overrides,
    };

    const hasChanged =
      !prev ||
      prev.ref !== nextEntry.ref ||
      prev.title !== nextEntry.title ||
      prev.description !== nextEntry.description ||
      prev.placement !== nextEntry.placement ||
      prev.align !== nextEntry.align;

    if (!hasChanged) return;

    registryRef.current[id] = nextEntry;
    setRegistryVersion((v) => v + 1);
  }, []);

  const unregisterTooltipTarget = useCallback((id) => {
    if (!registryRef.current[id]) return;

    delete registryRef.current[id];
    setRegistryVersion((v) => v + 1);
  }, []);

  const getNextAvailableTooltip = useCallback(() => {
    for (const tooltip of tooltips) {
      if (hasSeenTooltip(tooltip.cookieKey)) continue;

      const registered = registryRef.current[tooltip.id];
      const el = registered?.ref?.current;

      if (!el) continue;
      if (!isElementVisibleInContainer(el, visibilityThreshold)) continue;

      return {
        ...tooltip,
        ...registered,
      };
    }

    return null;
  }, [tooltips]);

  useEffect(() => {
    if (!enabled) return;

    const notifyViewportChange = () => {
      setViewportVersion((v) => v + 1);
    };

    window.addEventListener('resize', notifyViewportChange);
    window.addEventListener('scroll', notifyViewportChange, true);

    return () => {
      window.removeEventListener('resize', notifyViewportChange);
      window.removeEventListener('scroll', notifyViewportChange, true);
    };
  }, [enabled]);

  useEffect(() => {
    clearScheduledTimeout();

    if (!enabled) {
      setReady(false);
      setActiveId(null);
      return;
    }

    timeoutRef.current = window.setTimeout(() => {
      setReady(true);
    }, initialDelay);

    return () => {
      clearScheduledTimeout();
    };
  }, [enabled, initialDelay, clearScheduledTimeout]);

  useEffect(() => {
    clearVisibilityDelayTimeout();

    if (!enabled || !ready || activeId) return;

    const next = getNextAvailableTooltip();
    if (!next) return;

    if (visibilityDelay <= 0) {
      setActiveId(next.id);
      return;
    }

    visibilityDelayTimeoutRef.current = window.setTimeout(() => {
      const stillNext = getNextAvailableTooltip();
      if (stillNext && stillNext.id === next.id) {
        setActiveId(next.id);
      }
    }, visibilityDelay);

    return () => {
      clearVisibilityDelayTimeout();
    };
  }, [
    enabled,
    ready,
    activeId,
    getNextAvailableTooltip,
    registryVersion,
    viewportVersion,
    visibilityDelay,
    clearVisibilityDelayTimeout,
  ]);

  const skipAllTooltips = useCallback(() => {
    markAllEditorTooltipsSeen();
    notifyEditorTooltipsChanged();
    onTooltipStateChange?.();

    setActiveId(null);
    setReady(false);
    clearScheduledTimeout();
    clearVisibilityDelayTimeout();
  }, [
    clearScheduledTimeout,
    clearVisibilityDelayTimeout,
    onTooltipStateChange,
  ]);

  const showTooltipsAgain = useCallback(() => {
    resetAllEditorTooltips();
    notifyEditorTooltipsChanged();
    onTooltipStateChange?.();

    setActiveId(null);
    setReady(false);
    clearScheduledTimeout();
    clearVisibilityDelayTimeout();

    timeoutRef.current = window.setTimeout(() => {
      setReady(true);
    }, initialDelay);
  }, [
    initialDelay,
    clearScheduledTimeout,
    clearVisibilityDelayTimeout,
    onTooltipStateChange,
  ]);

  const closeTooltip = useCallback(
    (id) => {
      const tooltip = tooltips.find((item) => item.id === id);
      if (!tooltip) return;

      markTooltipSeen(tooltip.cookieKey, 365);
      notifyEditorTooltipsChanged();
      onTooltipStateChange?.();
      setActiveId(null);
      clearScheduledTimeout();

      if (!enabled) return;

      const delay = tooltip.delayAfterClose ?? stepDelay;

      setReady(false);

      timeoutRef.current = window.setTimeout(() => {
        setReady(true);
      }, delay);
    },
    [tooltips, enabled, stepDelay, clearScheduledTimeout],
  );

  const activeTooltip = useMemo(() => {
    if (!enabled || !activeId) return null;

    const baseTooltip = tooltips.find((item) => item.id === activeId);
    const registeredTooltip = registryRef.current[activeId];

    if (!baseTooltip || !registeredTooltip?.ref?.current) return null;

    return {
      ...baseTooltip,
      ...registeredTooltip,
    };
  }, [enabled, activeId, tooltips, registryVersion]);

  return useMemo(
    () => ({
      activeTooltip,
      activeId,
      registerTooltipTarget,
      unregisterTooltipTarget,
      closeTooltip,
      skipAllTooltips,
      showTooltipsAgain,
    }),
    [
      activeTooltip,
      activeId,
      registerTooltipTarget,
      unregisterTooltipTarget,
      closeTooltip,
      skipAllTooltips,
      showTooltipsAgain,
    ],
  );
}
